import { memo, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { FXFormFieldConfig } from 'types/operations';

import { BicTextFormField } from './BicTextFormField';
import { HiddenFormField } from './HiddenFormField';
import { SelectFormField } from './SelectFormField';
import { TextFormField } from './TextFormField';

interface FormBuilderProps {
	fields: [string, FXFormFieldConfig][];
	disabled?: boolean;
}

export const FormBuilder = memo(({ fields, disabled }: FormBuilderProps) => {
	const {
		setValue,
		formState: { submitCount },
	} = useFormContext();

	const prevSubmitCount = useRef<number | undefined>(undefined);
	const [ibanBicMask, setIbanBicMask] = useState<string | null>(null);

	useEffect(() => {
		if (submitCount === 0) {
			fields.forEach(([fieldKey, fieldConfig]) => {
				if (fieldConfig.value !== undefined) {
					setValue(fieldKey, fieldConfig.value);
				}
			});
		}
		prevSubmitCount.current = submitCount;
	}, [fields, submitCount, setValue]);

	return (
		<div className="space-y-space-16">
			{fields.map(([fieldKey, fieldConfig]) => {
				const isHidden =
					fieldConfig.hidden ||
					fieldKey.includes('beneficiary.address.type') ||
					fieldKey.includes('beneficiary.bank.address.type');

				if (isHidden) {
					return (
						<HiddenFormField
							key={fieldKey}
							fieldConfig={fieldConfig}
							fieldKey={fieldKey}
						/>
					);
				}

				if (fieldConfig.type === 'select') {
					return (
						<SelectFormField
							key={fieldKey}
							disabled={disabled}
							fieldConfig={fieldConfig}
							fieldKey={fieldKey}
						/>
					);
				}

				if (fieldKey.endsWith('.bic')) {
					return (
						<BicTextFormField
							key={fieldKey}
							disabled={disabled}
							fieldConfig={fieldConfig}
							fieldKey={fieldKey}
							ibanBicMask={ibanBicMask}
						/>
					);
				}

				return (
					<TextFormField
						key={fieldKey}
						disabled={disabled}
						fieldConfig={fieldConfig}
						fieldKey={fieldKey}
						onIbanBicMaskChange={setIbanBicMask}
					/>
				);
			})}
		</div>
	);
});

FormBuilder.displayName = 'FormBuilder';
